import FloatingInput from "@/components/ui/floatingInput";
import { Form, FormControl, FormField } from "@/components/ui/form/form";
import SubmitButton from "@/components/ui/submit-button";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/context/auth-context";
import { userKeys } from "@/features/auth/keys";
import useUpdateGuestEmail from "@/features/register/hooks/useUpdateGuestEmail";
import { queryClient } from "@/query-client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const emailFormSchema = z.object({
	email: z
		.string()
		.min(2, { message: "Email must be at least 2 characters." })
		.trim()
		.email({ message: "Please enter a valid email address." }),
});

type EmailFormSchema = z.infer<typeof emailFormSchema>;

export type VerifyEmailChangeEmailSectionProps = {
	resendEmailButton: ReactNode;
};

export function VerifyEmailChangeEmailSection({
	resendEmailButton,
}: VerifyEmailChangeEmailSectionProps) {
	const { user } = useAuth();
	const { toast } = useToast();
	const router = useRouter();
	const [isEditingEmail, setIsEditingEmail] = useState(false);

	const updateEmail = useUpdateGuestEmail();

	const emailForm = useForm<EmailFormSchema>({
		resolver: zodResolver(emailFormSchema),
		mode: "onBlur",
		defaultValues: {
			email: "",
		},
	});

	useEffect(() => {
		if (!isEditingEmail) return;
		emailForm.reset({
			email: user.email ?? "",
		});
		const focusTimer = setTimeout(() => {
			document.getElementById("Email address")?.focus();
		}, 100);
		return () => clearTimeout(focusTimer);
	}, [isEditingEmail, user.email, emailForm]);

	const handleSaveEmail = (values: EmailFormSchema) => {
		const trimmed = values.email.trim();
		updateEmail.mutate(
			{
				guestId: user.id,
				email: trimmed,
			},
			{
				onSuccess: async (updatedUser) => {
					queryClient.setQueryData(userKeys.userInformation(), updatedUser);
					await router.invalidate();
					setIsEditingEmail(false);
					toast({
						title: "Email updated",
						description:
							"Your email was saved. Tap below to resend the verification link.",
					});
				},
				onError: (error) => {
					toast({
						title: "Couldn’t update email",
						description: error.message,
						variant: "destructive",
					});
				},
			},
		);
	};

	if (isEditingEmail) {
		return (
			<Form {...emailForm}>
				<form
					className="w-full space-y-4 text-left mb-8"
					onSubmit={emailForm.handleSubmit(handleSaveEmail)}
				>
					<FormField
						control={emailForm.control}
						name="email"
						render={({ field }) => (
							<FormControl>
								<FloatingInput
									label="Email address"
									type="email"
									value={field.value}
									onChange={field.onChange}
									onBlur={field.onBlur}
									error={emailForm.formState.errors.email}
								/>
							</FormControl>
						)}
					/>
					<SubmitButton
						type="submit"
						testId="save-email-button"
						mutationStatus={updateEmail.status}
						loadingText="Saving..."
						className="w-full text-base font-semibold text-white bg-primary-600 rounded-xl py-3"
					>
						Save email address
					</SubmitButton>
					<button
						type="button"
						className="w-full text-sm font-semibold text-textDark py-2"
						onClick={() => {
							setIsEditingEmail(false);
							emailForm.reset();
						}}
					>
						Cancel
					</button>
				</form>
			</Form>
		);
	}

	return (
		<>
			<div className="w-full rounded-xl border border-border bg-muted/30 px-4 py-3 mb-3 text-left">
				<p className="text-xs font-medium text-textLight uppercase tracking-wider mb-1">
					Your email
				</p>
				<p className="text-lg font-semibold text-textDark break-all">
					{user.email}
				</p>
			</div>
			<button
				type="button"
				className="text-sm font-semibold text-primary-600 mb-8 w-full text-center"
				onClick={() => setIsEditingEmail(true)}
				data-testid="edit-email-button"
			>
				Wrong email? Update it
			</button>
			{resendEmailButton}
		</>
	);
}
